import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ✅ Checkout Abandonment Detection Hook
 * 
 * This hook provides comprehensive checkout abandonment detection and cleanup:
 * 1. Detects page navigation away from checkout
 * 2. Handles browser close/refresh events
 * 3. Monitors checkout timeout/expiration
 * 4. Automatically releases tickets when abandonment is detected
 * 
 * Usage:
 * const { isAbandoning } = useCheckoutAbandonment(orderId, {
 *   onAbandon: () => console.log('Checkout abandoned'),
 *   enabled: true
 * });
 */
export const useCheckoutAbandonment = (orderId, options = {}) => {
    const {
        onAbandon,
        enabled = true,
        excludePaths = ['/checkout/success', '/checkout/complete'], // Don't abandon on success pages
        debounceMs = 100 // Debounce rapid navigation events
    } = options;

    const location = useLocation();
    const abandonmentInProgress = useRef(false);
    const debounceTimeout = useRef(null);
    const hasAbandonedRef = useRef(false);

    /**
     * Call the abandonment API to immediately release tickets
     */
    const abandonCheckout = useCallback(async (reason = 'USER_NAVIGATION') => {
        if (!orderId || !enabled || abandonmentInProgress.current || hasAbandonedRef.current) {
            return;
        }

        abandonmentInProgress.current = true;
        hasAbandonedRef.current = true;

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                console.warn('No auth token found for checkout abandonment');
                return;
            }

            console.log(`🚨 Abandoning checkout for order: ${orderId} (${reason})`);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/checkout/${orderId}/abandon`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    // Use keepalive to ensure request completes even if page is unloading
                    keepalive: true
                }
            );

            if (response.ok) {
                console.log(`✅ Successfully abandoned checkout for order: ${orderId}`);
                onAbandon?.();
            } else {
                console.warn(`⚠️ Failed to abandon checkout for order: ${orderId}`, response.status);
            }

        } catch (error) {
            // Don't throw errors during abandonment as it might be during page unload
            console.warn(`⚠️ Error abandoning checkout for order: ${orderId}`, error);
        } finally {
            abandonmentInProgress.current = false;
        }
    }, [orderId, enabled, onAbandon]);

    /**
     * Debounced abandonment to prevent rapid calls
     */
    const debouncedAbandon = useCallback((reason) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            abandonCheckout(reason);
        }, debounceMs);
    }, [abandonCheckout, debounceMs]);

    /**
     * Check if the current path should trigger abandonment
     */
    const shouldAbandon = useCallback((pathname) => {
        // Don't abandon if we're on success/completion pages
        if (excludePaths.some(path => pathname.includes(path))) {
            return false;
        }

        // Don't abandon if we're still on checkout pages
        if (pathname.includes('/checkout')) {
            return false;
        }

        return true;
    }, [excludePaths]);

    // Handle navigation away from checkout
    useEffect(() => {
        const currentPath = location.pathname;

        // If we navigate away from checkout (and it's not to a success page), abandon
        if (orderId && shouldAbandon(currentPath)) {
            debouncedAbandon('NAVIGATION_AWAY');
        }
    }, [location.pathname, orderId, shouldAbandon, debouncedAbandon]);

    // Handle browser close/refresh events
    useEffect(() => {
        if (!orderId || !enabled) return;

        const handleBeforeUnload = (event) => {
            if (!hasAbandonedRef.current) {
                // Use synchronous beacon API for reliability during page unload
                const token = localStorage.getItem('authToken');
                if (token) {
                    const abandonUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/checkout/${orderId}/abandon`;

                    // Use sendBeacon for better reliability during page unload
                    if (navigator.sendBeacon) {
                        const headers = new Headers();
                        headers.append('Content-Type', 'application/json');
                        headers.append('Authorization', `Bearer ${token}`);

                        const blob = new Blob(['{}'], { type: 'application/json' });
                        navigator.sendBeacon(abandonUrl, blob);
                    } else {
                        // Fallback to synchronous fetch
                        fetch(abandonUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: '{}',
                            keepalive: true
                        }).catch(() => {
                            // Ignore errors during page unload
                        });
                    }

                    hasAbandonedRef.current = true;
                    console.log(`🚨 Browser unload - abandoned checkout for order: ${orderId}`);
                }
            }
        };

        const handleVisibilityChange = () => {
            // If page becomes hidden and we haven't abandoned yet, consider it abandonment
            if (document.hidden && !hasAbandonedRef.current) {
                debouncedAbandon('PAGE_HIDDEN');
            }
        };

        // Add event listeners
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [orderId, enabled, debouncedAbandon]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    return {
        abandonCheckout,
        isAbandoning: abandonmentInProgress.current
    };
};

export default useCheckoutAbandonment;
